import { synchronousHash } from '@soulhx/fs-common';

export interface InputObjectType {
  lang: string;
  msg: string;
}

export interface OutputObjectType {
  msg: string;
  msgHash: string;
}

/**
 * Given a list of "texts" (each with a language and a piece of text),
 * choose the best option given the user's browser prefs and the
 * languages supported by the i18n framework. Will always return at
 * least some text, even if there isn't a perfect choice.
 *
 * Defaults to using `en` if no "better" match was found, or, if there
 * isn't an `en` text, the first item in the array.
 *
 * Returns an empty string for an empty input array of texts.
 *
 * @param texts List of texts, with their associated language
 * @param preferred List of languages supported by the browser (typically pass `i18n.languages` for this)
 * @returns Object containing the best text to use (based on the user's language and the texts available), and a hash for that text
 */
export const getTextForI18n = (texts: InputObjectType[], preferred: readonly string[]): OutputObjectType => {
  if (texts.length < 1) return { msg: '', msgHash: synchronousHash('') };

  for (const pref of preferred) {
    const hit = texts.find((t) => t.lang.toLowerCase() === pref.toLowerCase());
    if (hit) return { msg: hit.msg, msgHash: synchronousHash(hit.msg) };
  }

  for (const pref of preferred) {
    const base = pref.split('-')[0].toLowerCase();
    const hit = texts.find((t) => t.lang.split('-')[0].toLowerCase() === base);
    if (hit) return { msg: hit.msg, msgHash: synchronousHash(hit.msg) };
  }

  const hit = texts.find((t) => t.lang.toLowerCase() === 'en');
  if (hit) return { msg: hit.msg, msgHash: synchronousHash(hit.msg) };

  return { msg: texts[0].msg, msgHash: synchronousHash(texts[0].msg) };
};
